// SCR-STORE целиком (E12-T10, E12-T11: 8 вкладок ux/08 §1.1, состояния §1.5) и поток оплаты (E12-T12):
// MOD-PURCHASE, MOD-PAY-PENDING ⛔, MOD-PAY-SUCCESS, MOD-PAY-ERROR — ux/08 §1–6. Ориентир «≈ ★ · ≈ ₽» и
// «не хватает N★» — E12-T22. Цены и состав — только из GET /shop и конфига: интерфейс чисел не хранит,
// цены из кэша не показываются (ux/08 §1.5). Вкладки кроме «Талонов» — store-goods.tsx.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Api, ResponseOf } from '@garages/contract';
import { useApp } from '../app/app.js';
import { devHooks } from '../app/dev-hooks.js';
import { amount, num } from '../i18n/format.js';
import { onWs } from '../net/ws.js';
import { policyFor } from '../net/error-policy.js';
import { baseRate, createAndOpen, guardInvoice, type InvoiceReport, isOnce, packLabel, PACK_NAMES, waitCredited, type PayDeps } from '../pay/flow.js';
import { online, setWallet, shop, wallet } from '../state/game.js';
import { inDebt, starsHint, tn, visibleTabs, STORE_TABS, type TabId, type TalonGood } from './store-model.js';
import { HintLine, RubNote, Stars, StarsIcon } from './store-ui.js';
import {
  BoostsTab, BoxesTab, EnergyTab, LavkaTab, LooksTab, MiscTab, SetsTab, TalonPurchaseBody, shopVersion, useExtras,
} from './store-goods.js';
import { ShowcasePurchaseBody } from './store-showcase.js';
import { storage } from '../state/storage.js';
import { versionAtLeast, type InvoiceStatus } from '../telegram/webapp.js';
import { stage, type ScreenProps, type SheetProps } from '../ui/App.js';
import { Btn, Dialog, ErrorBanner, ResourceHeader, Sheet, useActionBar } from '../ui/kit.js';
import { ApiCallError } from '../net/api-client.js';
import { useLoad } from '../ui/use-load.js';
import { I } from '../assets/icons.js';
import { t as tr } from '../i18n/t.js';
import { openInvoiceAny, type InvoiceEnv, type OpenPath } from '../pay/invoice-open.js';
import { sendNow, track } from '../analytics/analytics.js';
import { lastInvoice } from '../ui/debug-panel.js';
import { flyGrant } from '../ui/fx.js';

type Product = ResponseOf<Api['getShop']>['sections'][number]['products'][number];
const PENDING_KEY = 'pendingOrder';

/**
 * Игрок вернулся в игру из окна счёта: Telegram activated (после deactivated), вкладка снова видна (после hidden),
 * фокус (после blur) или касание страницы — пока окно счёта открыто, коснуться игры нельзя.
 */
function invoiceReturn(wa: NonNullable<ReturnType<typeof useApp>['wa']>, cb: () => void): () => void {
  let away = { tg: false, vis: false, focus: false };
  const onDeact = () => { away = { ...away, tg: true }; };
  const onAct = () => { if (away.tg) cb(); };
  const onVis = () => { if (document.visibilityState === 'hidden') away = { ...away, vis: true }; else if (away.vis) cb(); };
  const onBlur = () => { away = { ...away, focus: true }; };
  const onFocus = () => { if (away.focus) cb(); };
  const onTouch = () => cb();
  wa.onEvent?.('deactivated', onDeact);
  wa.onEvent?.('activated', onAct);
  document.addEventListener('visibilitychange', onVis);
  window.addEventListener('blur', onBlur);
  window.addEventListener('focus', onFocus);
  document.addEventListener('pointerdown', onTouch, true);
  return () => {
    wa.offEvent?.('deactivated', onDeact);
    wa.offEvent?.('activated', onAct);
    document.removeEventListener('visibilitychange', onVis);
    window.removeEventListener('blur', onBlur);
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('pointerdown', onTouch, true);
  };
}

/**
 * Итог окна счёта — в аналитику (словарь tech/09 §3: invoice_closed {order_id, status, ms_open}; платформа и версия
 * WebApp — в ctx пачки). Путь и запасной путь — в status («failed>link>paid», «cancelled:timeout»). Быстрый failed
 * (окно не показалось) — ещё и client_error kind=invoice с режимом окна: так сбой виден на сервере.
 */
function reportInvoice(wa: ReturnType<typeof useApp>['wa'], orderId: string | null, r: InvoiceReport, opened: { path: OpenPath; error?: string }): void {
  const end = r.via === 'callback' || r.via === 'event' ? r.status : `${r.status}:${r.via}`;
  const how = opened.path === 'openInvoice' ? '' : `${opened.path}>`;
  const status = r.fallback && r.first ? `${how}${r.first.status}>link>${end}` : `${how}${end}`;
  track('invoice_closed', { order_id: orderId, status, ms_open: r.ms });
  lastInvoice.value = { ...opened, status: r.status, via: r.via, ms: r.ms, fallback: r.fallback };
  // Окно открылось не штатно (исключение WebApp, версия ниже 6.1, быстрый failed) — подробности для разбора.
  if (r.first || opened.path !== 'openInvoice' || opened.error) {
    const w = wa as (NonNullable<typeof wa> & { isExpanded?: boolean }) | null;
    track('client_error', {
      kind: 'invoice',
      message: `${opened.error ? `openInvoice: ${opened.error}; ` : ''}path=${opened.path}${r.first ? `; first ${r.first.status} in ${r.first.ms} ms; fallback=link` : ''}; ${w?.platform ?? 'no-wa'} ${w?.version ?? '-'}; fullscreen=${w?.isFullscreen ? 1 : 0} expanded=${w?.isExpanded ? 1 : 0}; end=${end}`.slice(0, 256),
      screen: 'MOD-PURCHASE',
      fingerprint: r.first ? 'invoice_fast_fail' : `invoice_${opened.path}`,
      fatal: false,
    });
  }
  void sendNow(); // не ждать 30 с до следующей пачки: окно Telegram могут закрыть раньше
}

export function usePayDeps(): PayDeps {
  const app = useApp();
  return {
    call: app.call,
    openInvoice: (url, onLate, onCancel, order) => {
      const wa = app.wa;
      const tg = (window as { Telegram?: { WebView?: InvoiceEnv['webView'] } }).Telegram;
      const env: InvoiceEnv = { wa, webView: tg?.WebView ?? null, windowOpen: (u) => window.open(u, '_blank') };
      let opened: ReturnType<typeof openInvoiceAny> = { path: 'none' };
      if (order) track('invoice_opened', { order_id: order.orderId, product_id: order.productId });
      return guardInvoice({
        open: (u, cb) => { opened = openInvoiceAny(env, u, cb); lastInvoice.value = { ...opened }; if (opened.path === 'none') throw new Error(opened.error ?? 'no way to open invoice'); },
        // Ниже 6.1 openTelegramLink уводит саму страницу игры на t.me — тогда не трогаем.
        openLink: (u) => { if (!wa?.openTelegramLink || !versionAtLeast(wa, '6.1')) return false; wa.openTelegramLink(u); return true; },
        ...(order ? { poll: order.poll } : {}),
        report: (r) => reportInvoice(wa, order?.orderId ?? null, r, opened),
        onClosed: (cb) => {
          if (!wa?.onEvent) return () => {};
          const h = (...a: unknown[]) => { const e = a[0] as { url?: string; status?: InvoiceStatus } | undefined; if (e?.url && e.status) cb(e.url, e.status); };
          wa.onEvent('invoiceClosed', h);
          return () => wa.offEvent?.('invoiceClosed', h);
        },
        onReturn: (cb) => (wa ? invoiceReturn(wa, cb) : () => {}),
        ...(onCancel ? { onCancel } : {}),
      }, url, onLate);
    },
    onCredited: (cb) => onWs((ev) => { if (ev.t === 'payment.credited') cb(ev.d.orderId, ev.d.grants, ev.d.wallet); }),
  };
}

function Tile(p: { pr: Product; rate: number; wide?: boolean; disabled: boolean; rub: number | null }) {
  const app = useApp();
  const label = packLabel(p.pr, p.rate);
  const talons = p.pr.grant.currencies?.talons ?? 0;
  const blocked = !!p.pr.blocked;
  return (
    <button
      class="card store-tile"
      style={p.wide ? { gridColumn: '1 / -1' } : undefined}
      data-testid={`pack-${p.pr.productId}`}
      onClick={() => app.nav.openSheet('MOD-PURCHASE', { product: p.pr, rate: p.rate })}
    >
      {label ? <span class={`badge store-badge ${isOnce(p.pr) ? '' : 'soft'}`}>{label}</span> : null}
      <I n="talons" size={p.wide ? 44 : 36} />
      <b style={{ fontSize: 18 }}>{PACK_NAMES[p.pr.productId] ?? p.pr.productId}</b>
      <span>{amount('talons', talons)}</span>
      <span class={`btn small ${p.disabled || blocked ? 'disabled' : 'accent'}`} style={{ width: '100%' }}>{p.disabled ? tr('UI-OFFLINE') : <Stars n={p.pr.priceXtr} />}</span>
      {p.disabled ? null : <HintLine hint={starsHint(p.pr.priceXtr, p.rub)} />}
    </button>
  );
}

const TAB_KEY = 'storeTab';

/** Магазин выключен админкой (`FEATURE_DISABLED`, `details.feature = store`): «Ларёк на переучёте до …» (§1.5). */
function storeOffUntil(err: unknown): string | null {
  if (!(err instanceof ApiCallError) || err.code !== 'FEATURE_DISABLED') return null;
  const d = err.details ?? {};
  if (d['feature'] !== undefined && d['feature'] !== 'store') return null;
  const until = typeof d['until'] === 'string' ? Date.parse(d['until']) : NaN;
  return Number.isFinite(until) ? new Date(until).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow' }) : '';
}

export function StoreScreen(p: ScreenProps) {
  const app = useApp();
  useEffect(() => { void stage.value?.show('none'); }, [stage.value]);
  const [offErr, setOffErr] = useState<string | null>(null);
  const load = useLoad(() => app.call('getShop', { noRetry: true }).catch((e: unknown) => { setOffErr(storeOffUntil(e)); throw e; }), {
    onData: (d) => { setOffErr(null); shop.value = { data: d, at: Date.now() }; },
  });
  // Перечитать витрину после покупки за талоны (пределы, «Куплено ✓»).
  const ver = shopVersion.value;
  useEffect(() => { if (ver) load.reload(); }, [ver]);
  const showcase = useLoad(() => app.call('getShowcase'));
  useEffect(() => { if (ver) showcase.reload(); }, [ver]);
  useActionBar({});
  const [pending, setPending] = useState(storage.getJson<{ orderId: string; name: string; at: number }>(PENDING_KEY));
  // «Приложение закрыли во время оплаты»: проверить незавершённый заказ при входе (ux/08 §3.1).
  useEffect(() => {
    if (!pending) return;
    app.call('getOrder', { params: { orderId: pending.orderId } }).then((o) => {
      if (o.status === 'credited' && o.credited) {
        storage.remove(PENDING_KEY); setPending(null); setWallet(o.credited.wallet);
        app.nav.openSheet('MOD-PAY-SUCCESS', { grants: o.credited.grants });
      } else if (o.status !== 'paid' && o.status !== 'pre_checkout_ok') { storage.remove(PENDING_KEY); setPending(null); }
    }).catch(() => {});
  }, []);
  const ex = useExtras(load.data);
  const tabs = visibleTabs({ paidBoxes: ex.paidBoxes });
  const asked = (p.params['tab'] as TabId | undefined) ?? (storage.get(TAB_KEY) as TabId | null) ?? 'talons';
  const [tab, setTabState] = useState<TabId>(tabs.includes(asked) ? asked : 'talons');
  const setTab = (t: TabId) => { app.haptic('selection'); setTabState(t); storage.set(TAB_KEY, t); };
  const cur = tabs.includes(tab) ? tab : 'talons';
  const products = load.data?.sections.find((s) => s.id === 'talons')?.products ?? [];
  const rate = baseRate(products);
  const starter = products.find((x) => isOnce(x));
  const rest = products.filter((x) => x !== starter);
  const off = !online.value || load.offline;
  const debt = inDebt(wallet.value);
  const spend = load.data?.spendLimit;
  const tabProps = load.data ? { shop: load.data, ex, off } : null;
  return (
    <>
      <ResourceHeader />
      <div class="content screen-bg store" data-testid="SCR-STORE" data-tab={cur}>
        <div class="page col store-page">
          {offErr !== null ? (
            <div class="empty store-stub card" data-testid="store-off"><I n="maint" size={48} class="ill" />{offErr ? tr('SHOP-OFF', { time: offErr }) : tr('CL-STORE-OFF-NOW')}</div>
          ) : (
            <>
              {pending ? <div class="banner" data-testid="pay-pending-banner">{tr('CL-SCREENS-STORE-01')}{pending.name}»…{Date.now() - pending.at > 15 * 60_000 ? tr('CL-SCREENS-STORE-02', { p0: pending.orderId }) : ''}</div> : null}
              {load.data?.paymentsOff ? <div class="banner" data-testid="pay-off-banner">{tr('SHOP-PAY-OFF')}</div> : null}
              {debt ? <div class="banner warn" data-testid="store-debt">{tr('CL-STORE-DEBT-BANNER', tn(wallet.value?.talonsDebt ?? Math.abs(Math.min(0, wallet.value?.talons ?? 0))))}</div> : null}
              {spend && spend.leftXtr === 0 ? <div class="banner" data-testid="spend-limit">{tr('CL-STORE-LIMIT-REACHED', { d: new Date(spend.resetsAt).toLocaleDateString('ru-RU') })}</div> : null}
              {load.error && !load.offline ? <ErrorBanner what={tr('CL-SCREENS-STORE-03')} onRetry={load.reload} /> : null}
              {load.offline ? <div class="banner" data-testid="store-offline">{tr('CL-SCREENS-STORE-04')}</div> : null}
              {load.data?.personalOffers.length && cur !== 'sets' ? (
                <button class="banner store-promo" data-testid="store-promo" onClick={() => setTab('sets')}><I n="party" size={22} /><span style={{ flex: 1 }}>{tr('SHOP-OFFER-BADGE')}</span><span class="accent-text">›</span></button>
              ) : null}
              <div class="store-top">
                <div class="chips store-tabs" role="tablist" data-testid="store-tabs">
                  {tabs.map((id) => (
                    <button key={id} class={`chip ${cur === id ? 'on' : ''}`} role="tab" aria-selected={cur === id} data-testid={`tab-${id}`} onClick={() => setTab(id)}>
                      {tr(STORE_TABS.find((x) => x.id === id)!.key)}
                      {id === 'sets' && load.data?.personalOffers.length ? <span class="tab-dot" aria-hidden /> : null}
                    </button>
                  ))}
                </div>
                <button class="chip purchases-link" data-testid="open-purchases" onClick={() => app.nav.push('SCR-PURCHASES')}>{tr('CL-STORE-PURCHASES')}</button>
              </div>
              <div class="store-panel" data-testid={`store-tab-${cur}`}>
                {!load.data && load.loading ? <div class="store-grid">{Array.from({ length: 6 }, (_, i) => <div key={i} class="skeleton" style={{ height: 120 }} />)}</div> : null}
                {cur === 'talons' && load.data ? (
                  products.length ? (
                    <div class="store-grid" data-testid="store-grid">
                      {starter ? <Tile pr={starter} rate={rate} wide disabled={off} rub={ex.rubPerStar} /> : null}
                      {rest.map((pr) => <Tile key={pr.productId} pr={pr} rate={rate} disabled={off} rub={ex.rubPerStar} />)}
                    </div>
                  ) : <div class="empty"><I n="shop" size={48} class="ill" />{tr('CL-SCREENS-STORE-05')}</div>
                ) : null}
                {tabProps && cur === 'sets' ? <SetsTab {...tabProps} /> : null}
                {tabProps && cur === 'shop' ? <LavkaTab {...tabProps} showcase={{ data: showcase.data, error: !!showcase.error }} /> : null}
                {tabProps && cur === 'boxes' ? <BoxesTab {...tabProps} /> : null}
                {tabProps && cur === 'energy' ? <EnergyTab {...tabProps} /> : null}
                {tabProps && cur === 'boosts' ? <BoostsTab {...tabProps} /> : null}
                {tabProps && cur === 'looks' ? <LooksTab {...tabProps} /> : null}
                {tabProps && cur === 'misc' ? <MiscTab {...tabProps} /> : null}
              </div>
              <p class="small muted store-note">{tr('CL-SCREENS-STORE-06')}{ex.rubPerStar ? ` · ${tr('CL-STORE-RUB-SHORT')}` : ''}</p>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/** MOD-PURCHASE: «Оплатить {n} ★» → заказ (один ключ на окно) → openInvoice → статус (ux/08 §3.1). */
export function PurchaseSheet({ entry }: SheetProps) {
  const good = entry.params['good'] as TalonGood | undefined;
  if (good) {
    return (
      <Sheet entry={entry}>
        <TalonPurchaseBody g={good} entryParams={entry.params} shop={shop.value?.data ?? null} />
      </Sheet>
    );
  }
  if (entry.params['showcase']) return <Sheet entry={entry}><ShowcasePurchaseBody entryParams={entry.params} /></Sheet>;
  return <StarPurchase entry={entry} />;
}

/** Покупка за ★: «Оплатить {n} ★» значком Telegram Stars, ориентир «≈ ₽», долг по талонам до оплаты (§2). */
function StarPurchase({ entry }: SheetProps) {
  const app = useApp();
  const pr = entry.params['product'] as Product;
  const rate = Number(entry.params['rate'] ?? 2);
  const returnTo = entry.params['returnTo'] as { id: string; params: Record<string, unknown> } | undefined;
  const deps = usePayDeps();
  const key = useRef(app.idem());
  const [busy, setBusy] = useState(false);
  const ex = useExtras(shop.value?.data ?? null);
  const talons = pr.grant.currencies?.talons ?? 0;
  const name = PACK_NAMES[pr.productId] ?? pr.productId;
  const label = packLabel(pr, rate);
  const debt = wallet.value?.talonsDebt ?? (wallet.value && wallet.value.talons < 0 ? -wallet.value.talons : 0);
  // «Оплата не прошла» — отмена, ошибка или окно закрыли без статуса (крестик у докупки звёзд): кнопка снова активна.
  const [notDone, setNotDone] = useState(false);
  // «Отменить» при открытом счёте (и тап по затемнению): в Telegram Web A о крестике окна счёта игра не узнаёт.
  const cancelRef = useRef<{ fired: boolean; cbs: Set<() => void> }>({ fired: false, cbs: new Set() });
  const onCancel = (cb: () => void) => {
    const c = cancelRef.current;
    if (c.fired) { cb(); return () => {}; }
    c.cbs.add(cb);
    return () => { c.cbs.delete(cb); };
  };
  const cancelInvoice = () => { const c = cancelRef.current; c.fired = true; [...c.cbs].forEach((f) => f()); c.cbs.clear(); };
  const toPending = (orderId: string, status: InvoiceStatus) => {
    storage.setJson(PENDING_KEY, { orderId, name, at: Date.now() });
    devHooks.invoicePaid?.(orderId);
    app.nav.closeSheets();
    app.nav.openSheet('MOD-PAY-PENDING', { orderId, mode: status === 'pending' ? 'pending' : 'paid', name, ...(returnTo ? { returnTo } : {}) });
  };
  const pay = async () => {
    if (busy) return;
    setBusy(true);
    setNotDone(false);
    cancelRef.current = { fired: false, cbs: new Set() };
    app.haptic('medium');
    try {
      let orderId = '';
      // Окно сочли закрытым, а игрок всё же оплатил (поздний статус) — ждём начисления как обычно.
      const late = (st: InvoiceStatus) => { if ((st === 'paid' || st === 'pending') && orderId) toPending(orderId, st); };
      const r = await createAndOpen(deps, pr.productId, key.current, late, onCancel);
      if (r.kind === 'error') {
        app.haptic('error');
        app.nav.openSheet('MOD-PAY-ERROR', { code: r.code, product: pr, rate, retryAfterS: r.retryAfterS }, { replace: true });
        return;
      }
      orderId = r.orderId;
      if (r.status === 'cancelled' || r.status === 'failed') { app.haptic('warning'); setNotDone(true); return; } // тот же заказ живёт 10 мин
      toPending(r.orderId, r.status);
    } finally { setBusy(false); }
  };
  const blocked = pr.blocked === 'payments_off' ? tr('CL-SCREENS-STORE-07') : pr.blocked === 'spend_limit' ? tr('CL-SCREENS-STORE-08') : null;
  const disabled = !online.value || !!blocked || busy;
  return (
    <Sheet entry={entry} title={name} onDismiss={() => { if (!busy) return false; cancelInvoice(); return true; }}>
      <div class="col" style={{ alignItems: 'center', textAlign: 'center' }} data-testid="purchase">
        <I n="talons" size={64} />
        <b style={{ fontSize: 22 }}>{amount('talons', talons)}</b>
        {label ? <span class="badge">{label}</span> : null}
        {isOnce(pr) ? <span class="small muted">{tr('CL-SCREENS-STORE-10')}</span> : null}
        <span class="small muted">{tr('CL-SCREENS-STORE-11')}{" "}{amount('talons', wallet.value?.talons ?? 0)}</span>
        {debt > 0 ? <div class="banner warn" data-testid="purchase-debt">{tr('CL-STORE-DEBT-PACK', { d: num(debt), k: num(Math.max(0, talons - debt)), n: num(talons) })}</div> : null}
        <div class="purchase-cta">
          <button class="btn accent block" data-testid="pay" disabled={disabled} aria-busy={busy} onClick={() => { void pay(); }}>
            {busy ? <span class="spinner" aria-hidden /> : null}
            {!online.value ? tr('UI-OFFLINE') : blocked ?? <>{tr('CL-SCREENS-STORE-09', { p0: num(pr.priceXtr) })}<StarsIcon size={18} /></>}
          </button>
          {busy ? <button class="btn outline block" data-testid="pay-cancel" onClick={cancelInvoice}>{tr('CL-PAY-CANCEL')}</button> : null}
        </div>
        {notDone ? <div class="banner warn" role="status" data-testid="pay-not-done">{tr('CL-PAY-NOT-DONE')}</div> : null}
        <HintLine hint={starsHint(pr.priceXtr, ex.rubPerStar)} testId="purchase-rub" />
        {ex.rubPerStar ? <RubNote /> : null}
        <p class="small muted" style={{ margin: 0 }}>{tr('CL-SCREENS-STORE-12')}{" "}<a href="#" onClick={(e) => { e.preventDefault(); app.wa?.openLink?.('https://example.invalid/legal/purchases'); }}>{tr('CL-SCREENS-STORE-13')}</a></p>
      </div>
    </Sheet>
  );
}

/** MOD-PAY-PENDING ⛔: «Свернуть — начислим сами» видна сразу; > 30 с — текст и «Поддержка» (ux/08 §4). */
export function PayPendingDialog({ entry }: SheetProps) {
  const app = useApp();
  const deps = usePayDeps();
  const orderId = String(entry.params['orderId']);
  const mode = entry.params['mode'] === 'pending' ? 'pending' : 'paid';
  const [long, setLong] = useState(false);
  useEffect(() => {
    const sig = { cancelled: false };
    const t = setTimeout(() => setLong(true), 30_000);
    void waitCredited(deps, orderId, mode, sig).then((r) => {
      if (sig.cancelled) return;
      if (r.kind === 'credited') {
        storage.remove(PENDING_KEY);
        setWallet(r.wallet);
        app.nav.closeSheets();
        app.nav.openSheet('MOD-PAY-SUCCESS', { grants: r.grants, ...(entry.params['returnTo'] ? { returnTo: entry.params['returnTo'] } : {}) });
      } else if (r.kind === 'failed') {
        storage.remove(PENDING_KEY);
        app.nav.closeSheets();
        app.nav.openSheet('MOD-PAY-ERROR', { code: 'failed' });
      } else setLong(true);
    });
    return () => { sig.cancelled = true; clearTimeout(t); };
  }, []);
  return (
    <Dialog
      title={long ? tr('CL-SCREENS-STORE-14') : tr('CL-SCREENS-STORE-15')}
      testId="MOD-PAY-PENDING"
      text={long ? tr('CL-SCREENS-STORE-16') : mode === 'pending' ? tr('CL-SCREENS-STORE-17') : tr('CL-SCREENS-STORE-18')}
      actions={[
        ...(long ? [{ text: tr('CL-SCREENS-STORE-19'), tone: 'outline' as const, onClick: () => app.wa?.openTelegramLink?.('https://t.me/garages_support_bot') }] : []),
        { text: tr('CL-SCREENS-STORE-20'), tone: 'outline', testId: 'pay-minimize', onClick: () => app.nav.closeSheets() },
      ]}
    >
      <div class="spinner" style={{ width: 28, height: 28, margin: '0 auto 12px' }} />
      <p class="small muted">{tr('CL-SCREENS-STORE-21')}{" "}{orderId.slice(0, 8)}</p>
    </Dialog>
  );
}

export function PaySuccessSheet({ entry }: SheetProps) {
  const app = useApp();
  const grants = entry.params['grants'] as { currencies?: Record<string, number> } | undefined;
  const t = grants?.currencies?.['talons'] ?? 0;
  // Покупка из нехватки (термос, товар за талоны): «Вернуться к покупке» открывает исходное окно (ux/08 §5).
  const back = entry.params['returnTo'] as { id: string; params: Record<string, unknown> } | undefined;
  const pic = useRef<HTMLDivElement>(null);
  // Талоны начислены — монеты летят из окна в пилюлю шапки (она под затемнением, но видна и вздрагивает).
  useEffect(() => { app.haptic('success'); const t = setTimeout(() => flyGrant(pic.current, grants?.currencies), 260); return () => clearTimeout(t); }, []);
  return (
    <Sheet entry={entry} actions={[back
      ? { text: tr('CL-STORE-BACK-TO-BUY'), testId: 'pay-ok', onClick: () => app.nav.openSheet(back.id as 'MOD-PURCHASE', back.params, { replace: true }) }
      : { text: tr('CL-SCREENS-STORE-22'), testId: 'pay-ok', onClick: () => app.nav.popSheet() }]}>
      <div class="col" style={{ alignItems: 'center', textAlign: 'center' }} data-testid="pay-success">
        <div class="pay-pic" ref={pic}><I n="party" size={64} /></div>
        <h2 style={{ margin: 0 }}>{tr('CL-SCREENS-STORE-23')}{" "}{num(t)} {" "}{tr('CL-SCREENS-STORE-24')}</h2>
        <span class="muted">{tr('CL-SCREENS-STORE-25')}{" "}{amount('talons', wallet.value?.talons ?? 0)}</span>
      </div>
    </Sheet>
  );
}

const PAY_ERROR_TEXT: Record<string, string> = {
  failed: tr('CL-SCREENS-STORE-26'),
  SOLD_OUT: tr('CL-SCREENS-STORE-27'),
  ALREADY_PURCHASED: tr('CL-SCREENS-STORE-28'),
  GONE: tr('CL-SCREENS-STORE-29'),
  PURCHASE_BLOCKED: tr('CL-SCREENS-STORE-30'),
  RATE_LIMITED: tr('CL-SCREENS-STORE-31'),
  INVOICE_UNAVAILABLE: tr('CL-SCREENS-STORE-32'),
  TG_LINK_REQUIRED: tr('CL-SCREENS-STORE-33'),
  INTERNAL: tr('CL-SCREENS-STORE-34'),
};

export function PayErrorSheet({ entry }: SheetProps) {
  const app = useApp();
  const code = String(entry.params['code'] ?? 'INTERNAL');
  const pr = entry.params['product'] as Product | undefined;
  const retryAfterS = Number(entry.params['retryAfterS'] ?? 0);
  const base = PAY_ERROR_TEXT[code] ?? (entry.params['text'] as string | undefined) ?? policyFor(code).text ?? PAY_ERROR_TEXT['INTERNAL']!;
  // 503 INVOICE_UNAVAILABLE с Retry-After: Bot API ответил 429 — говорим, когда повторить (ux/08 §6)
  const text = retryAfterS > 0 && (code === 'INVOICE_UNAVAILABLE' || code === 'RATE_LIMITED') ? tr('CL-SCREENS-STORE-35', { p0: base, p1: retryAfterS }) : base;
  const retry = (code === 'failed' || code === 'INVOICE_UNAVAILABLE' || code === 'INTERNAL' || code === 'NETWORK' || code === 'TIMEOUT') && pr;
  useEffect(() => { app.haptic('error'); }, []);
  return (
    <Sheet entry={entry} title={tr('CL-SCREENS-STORE-36')} actions={[
      { text: tr('UI-BTN-CLOSE'), tone: 'outline', onClick: () => app.nav.popSheet() },
      ...(retry ? [{ text: tr('CL-SCREENS-STORE-37'), testId: 'pay-retry', onClick: () => app.nav.openSheet('MOD-PURCHASE', { product: pr, rate: entry.params['rate'] ?? 2 }, { replace: true }) }] : []),
      ...(code === 'PURCHASE_BLOCKED' ? [{ text: tr('CL-SCREENS-STORE-19'), onClick: () => app.wa?.openTelegramLink?.('https://t.me/garages_support_bot') }] : []),
    ]}>
      <p data-testid="pay-error-text">{text}</p>
    </Sheet>
  );
}

export { Btn };
